// Needed only during development for some reason. More info on github issue
// import 'firebase/auth';
import 'firebase/compat/auth';

import { Injectable } from '@angular/core';
import { Auth, authState, signInWithCustomToken, User,  } from "@angular/fire/auth";
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { switchMap, map } from 'rxjs/operators';
import { Observable, from } from 'rxjs';
import { collection, collectionData, doc, docData, Firestore, query, Timestamp, updateDoc, where } from '@angular/fire/firestore';
import { collectionRef, docRef } from '../shared/utils';
import { CompletionSurvey, Quotas } from 'core-lib';

export interface Survey_ad {
  dataFields: { [id: string]: string },
  urlOpen: boolean;
  counterFields: {
    queId: string,
    answers: string[]
  }[];
}

export interface AnswerTracker {
  queId: string;
  value: string;
}

export interface DayStats {
  completedSessions: number;
  date: Timestamp;
  totalSessions: number;
}

interface CompletionCounters { totalCompletions: number, totalTerminates: number, otherCounts: { [answerId: string]: number } }

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {

  user$: Observable<User>;
  user: User;

  // Is auth status initializing
  pending: boolean = true;

  constructor(
    private afAuth: Auth,
    private http: HttpClient,
    private fs: Firestore
  ) {
    this.user$ = authState(this.afAuth);
    this.user$.subscribe(user => {
      this.user = user;
      this.pending = false;
    }, err => {
      this.logout();
    });
  }

  login(token: string) {
    return this.http.post<{ token: string }>(`${environment.authURL}/authenticate`, { token })
      .pipe(
        switchMap(result => from(signInWithCustomToken(this.afAuth, result.token)))
      );
  }

  logout() {
    this.pending = true;
    return this.afAuth.signOut();
  }

  get surveys$() {
    // TODO: Properly add generic types when the angularfire plugin is fixed...
    return collectionData(collectionRef<Survey_ad & { id: string }>(this.fs, 'surveys'), { idField: 'id' });
  }

  get todayStats() {
    const myDate = new Date();
    myDate.setDate(myDate.getDate() - 2);
    myDate.setHours(0);
    myDate.setMinutes(0);
    myDate.setSeconds(0);
    // return this.fs.collection<DayStats>('dayStats', qf => qf
    return collectionData(query(collectionRef<DayStats>(this.fs, 'surveys'), where('date', '>=', myDate)))
      .pipe(map(days => days?.[0]));
  }

  partialsFor(surveyId: string) {
    // return this.fs.doc<{ currentPartials: number, surveyId: string }>(`partials/${surveyId}`)
    return docData(docRef<{ currentPartials: number, surveyId: string }>(this.fs, `partials/${surveyId}`), { idField: 'surveyId' })
      .pipe(map(result => result ? result : { currentPartials: 0, surveyId }));
  }

  completionsFor(surveyId: string) {
    // return this.fs.doc<CompletionSurvey>(`completions/${surveyId}`).valueChanges();
    return docData(docRef<CompletionSurvey>(this.fs, `completions/${surveyId}`));
  }

  completesFor(surveyId: string) {
    return docData(docRef<CompletionCounters>(this.fs, `completions/${surveyId}`), { idField: 'surveyId' } as any)
      .pipe(map((result: CompletionCounters & { surveyId: string }) => !!result ? ({
          ...result,
          totalCompletions: result.totalCompletions || 0,
          totalTerminates: result.totalTerminates || 0
      }) : { totalCompletions: 0, surveyId, otherCounts: {}, totalTerminates: 0 }));
  }

  initMetricsFor(surveyId: string) {
    return this.http.get(`${environment.adminURL}/metrics/${surveyId}/init`);
  }

  answerTrackers(surveyId: string) {
    return collectionData(collectionRef<AnswerTracker>(this.fs, `surveys/${surveyId}/answerTrackers`), { idField: 'id' } as any);
  }

  surveyMeta(surveyId: string) {
    return docData(docRef<Survey_ad>(this.fs, `surveys/${surveyId}`));
  }

  counterMeta(surveyId: string) {
    // return this.fs.collection(`surveys/${surveyId}/answerTrackers`).valueChanges({ idField: 'id' })
    return collectionData(collection(this.fs, `surveys/${surveyId}/answerTrackers`), { idField: 'id' })
      .pipe(map(trackers => trackers.reduce((allTrackers, tracker) => ({
        ...allTrackers,
        [tracker.id]: tracker
      }), {} as { [trackerId: string]: AnswerTracker })));
  }

  quotasFor(surveyId: string) {
    // return this.fs.doc<Quotas>(`quotas/${surveyId}`).valueChanges();
    return docData(docRef<Quotas>(this.fs, `quotas/${surveyId}`));
  }

  updateQuotaTarget(surveyId: string, segment: string, targetCount: number, currentCount: number) {
    // return this.fs.doc(`quotas/${surveyId}`).update({ [`${segment}.targetCount`]: targetCount, [`${segment}.met`]: currentCount >= targetCount });
    return updateDoc(doc(this.fs, `quotas/${surveyId}`), { [`${segment}.targetCount`]: targetCount, [`${segment}.met`]: currentCount >= targetCount });
  }

}
